import React, { useState, forwardRef, useEffect } from 'react'
import CheckCircleOutlineSharpIcon from '@mui/icons-material/CheckCircleOutlineSharp'
import HighlightOffSharpIcon from '@mui/icons-material/HighlightOffSharp'
import ErrorOutlineSharpIcon from '@mui/icons-material/ErrorOutlineSharp'
import { East, Close, ArrowDropDown } from '@mui/icons-material'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import FilterListIcon from '@mui/icons-material/FilterList'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InfoIcon from '@mui/icons-material/Info'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  Button,
  Tooltip,
  IconButton,
  Slide,
  MenuItem,
  CircularProgress,
  Popover,
  TextField,
  Checkbox
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { stateNameMap } from '../../utils/stateNames'

const Transition = forwardRef(function Transition (props, ref) {
  return <Slide direction='left' ref={ref} {...props} />
})

function AnalysisCard ({ cardData, cardType, selectedLob }) {
  const [openPopup, setOpenPopup] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [actionTaken, setActionTaken] = useState(null)
  const [appetiteInfo, setAppetiteInfo] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [borderColor, setBorderColor] = useState('#ccc')
  const [sortDirection, setSortDirection] = useState('desc') // 'desc' means high relevancy first
  const [sortColumn, setSortColumn] = useState('relevancy') // or 'cmi'
  const [cmiFilter, setCmiFilter] = useState([])
  const [cmiFilterAnchor, setCmiFilterAnchor] = useState(null)
  const [cmiSearch, setCmiSearch] = useState('')
  const [infoAnchor, setInfoAnchor] = useState(null) // New state for info popover
  const open = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleIconClick = () => {
    setOpenPopup(true)
  }

  const handleClosePopup = () => {
    setOpenPopup(false)
    setActionTaken('')
  }

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleActionClick = action => {
    setActionTaken(action)
    handleMenuClose()
  }

  const toggleSort = () => {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const mapRelevancy = r => {
    if (!r) return 0
    const lower = r.toLowerCase()
    if (lower === 'high') return 3
    if (lower === 'medium') return 2
    if (lower === 'low') return 1
    return 0
  }

  const getRelevancyIcon = relevancy => {
    const r = relevancy?.toLowerCase()
    if (r === 'high')
      return (
        <KeyboardDoubleArrowUpIcon
          style={{ color: 'green' }}
          fontSize='small'
        />
      )
    else if (r === 'medium')
      return (
        <KeyboardDoubleArrowRightIcon
          style={{ color: 'orange' }}
          fontSize='small'
        />
      )
    else if (r === 'low')
      return (
        <KeyboardDoubleArrowDownIcon
          style={{ color: 'red' }}
          fontSize='small'
        />
      )
    return null
  }

  useEffect(() => {
    setIsLoading(true)
    if (cardData) {
      setTimeout(() => {
        setAppetiteInfo(cardData)
      }, 1000)
    } else {
      setAppetiteInfo({})
    }
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)

    if (cardType === 'Do Not Write' && cardData?.Summary?.score) {
      setBorderColor(cardData?.Summary?.score === 10 ? 'green' : 'red')
    } else {
      if (cardData?.Summary?.score < 5) {
        setBorderColor('red')
      } else if (cardData?.Summary?.score < 7) {
        setBorderColor('orange')
      } else if (cardData?.Summary?.score >= 7) {
        setBorderColor('green')
      }
    }
  }, [cardData, cardType, selectedLob])

  function sentenceCase (str) {
    if (!str) return ''
    const lower = str.toLowerCase()
    return lower.charAt(0).toUpperCase() + lower.slice(1)
  }

  // Compute unique CMIs from appetiteInfo.AppetiteDetails:
  const uniqueCMIs = Array.from(
    new Set(
      Object.keys(appetiteInfo?.AppetiteDetails || {}).map(
        key => appetiteInfo.AppetiteDetails[key].cmi
      )
    )
  )

  const filteredCMIs = uniqueCMIs.filter(cmi =>
    cmi?.toString().toLowerCase().includes(cmiSearch.toLowerCase())
  )

  // Build details array and apply sorting based on the current sortColumn &
  // sortDirection, then filter if needed.
  const detailsArray = Object.keys(appetiteInfo?.AppetiteDetails || {}).map(
    key => ({ key, ...appetiteInfo.AppetiteDetails[key] })
  )

  let sortedDetails = []
  if (sortColumn === 'cmi') {
    sortedDetails = detailsArray.sort((a, b) =>
      sortDirection === 'asc' ? a.cmi - b.cmi : b.cmi - a.cmi
    )
  } else {
    sortedDetails = detailsArray.sort((a, b) => {
      const diff = mapRelevancy(a.relevancy) - mapRelevancy(b.relevancy)
      return sortDirection === 'asc' ? diff : -diff
    })
  }

  // Apply filter on the CMI column if any filters are selected
  if (cmiFilter.length > 0) {
    sortedDetails = sortedDetails.filter(detail =>
      cmiFilter.includes(detail.cmi)
    )
  }

  const filteredDetails = sortedDetails.filter(detail =>
    cmiFilter.length > 0 ? cmiFilter.includes(detail.cmi) : true
  )

  // New functions to handle Info icon popover.
  const handleInfoClick = event => {
    setInfoAnchor(event.currentTarget)
  }

  const handleInfoClose = () => {
    setInfoAnchor(null)
  }

  // Dummy content based on cardType
  const infoContent = cardType.includes('Comments')
    ? 'This card contains all the generated comments from the uploaded applications, along with useful insights extracted using LLMs.'
    : cardType.includes('Desk')
    ? 'This card provides detailed guidance and extracted information from backend processing by LLMs. It offers useful insights for application analysis.'
    : 'This card provides detailed information about the do not write status of the application. It includes insights and recommendations based on the analysis of the uploaded applications.'

  return (
    <div className='w-2/4 stage-types'>
      <h2 className='mb-2 text-xl text-center text-black text-orange-600'>
        {cardType}
        <IconButton
          onClick={handleInfoClick}
          size='small'
          className='text-orange-600'
        >
          <InfoIcon fontSize='small' className='text-orange-600' />
        </IconButton>
      </h2>
      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <div style={{ padding: '16px', maxWidth: '300px' }}>
          <p style={{ margin: 0 }}>{infoContent}</p>
        </div>
      </Popover>
      <div
        className={`flex flex-col p-3 max-w-full text-sm leading-5 border-b-8 shadow-lg ${borderColor}`}
        style={{
          border: '1px solid #c3c3c3',
          borderBottom: `1px solid ${borderColor}`,
          borderRadius: '1rem',
          fontWeight: '500',
          background:
            cardData?.Summary?.color === 'yellow'
              ? 'lightyellow'
              : cardData?.Summary?.color === 'red'
              ? '#ff000012'
              : cardData?.Summary?.color === 'green'
              ? '#d9f4e8'
              : cardType.includes('Desk')
              ? 'moccasin'
              : cardType.includes('Comments')
              ? 'mistyrose'
              : 'white',
          minHeight: '300px'
        }}
      >
        <div
          className='appetite-content flex flex-col items-start justify-between px-4 mt-4'
          style={{ minHeight: '200px', height: '225px', overflowY: 'auto' }}
        >
          {isLoading ? (
            <div className='flex justify-center items-center w-full h-full p-4'>
              <CircularProgress color='warning' />
            </div>
          ) : appetiteInfo?.Summary?.AppetiteSummary ? (
            <div
              dangerouslySetInnerHTML={{
                __html: appetiteInfo.Summary.AppetiteSummary
              }}
            />
          ) : (
            'The data is not available.'
          )}
        </div>
        {cardType !== 'Do Not Write' &&
          appetiteInfo?.Summary?.AppetiteSummary && (
            <East
              className='text-black self-end cursor-pointer'
              fontSize='medium'
              onClick={handleIconClick}
              style={{ cursor: 'pointer' }}
            />
          )}
      </div>
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        TransitionComponent={Transition}
        fullWidth
        maxWidth='900px'
        PaperProps={{
          style: {
            border: '2px solid #ea580c',
            borderBottom: '5px solid #ea580c',
            boxShadow: '0px 4px 10px rgba(255, 165, 0, 0.4)',
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            margin: 0,
            width: '900px',
            borderRadius: '10px',
            background: 'linear-gradient(to right, #f8f9fa, #e9ecef)'
          }
        }}
      >
        <DialogTitle className='flex justify-between items-center'>
          <h2 className='text-xl text-black text-orange-600'>{cardType}</h2>
          {appetiteInfo?.['Summary']?.Escalatable && !actionTaken && (
            <div
              className='flex items-center cursor-pointer text-sm font-normal ml-auto'
              onClick={handleMenuClick}
            >
              <span>Action</span>
              <ArrowDropDown />
            </div>
          )}
          {appetiteInfo?.['Summary']?.Escalatable && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                style: {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Tooltip title='Actions' arrow>
                <IconButton onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={() => handleActionClick('Escalate')}>
                  Escalate
                </MenuItem>
              </Menu>
            </Menu>
          )}
          <IconButton onClick={handleClosePopup}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className='grid grid-cols-1 gap-4'>
            {!actionTaken &&
              cardType.includes('Comments') &&
              Object.keys(appetiteInfo).length > 0 && (
                <>
                  <div className='space-y-4'>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed'
                      }}
                    >
                      <thead className='text-orange-600'>
                        <tr>
                          {/* CMI Column Header with sort and filter */}
                          <th
                            style={{
                              border: '1px solid #ccc',
                              width: '10%',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (sortColumn === 'cmi') {
                                setSortDirection(prev =>
                                  prev === 'asc' ? 'desc' : 'asc'
                                )
                              } else {
                                setSortColumn('cmi')
                                setSortDirection('asc')
                              }
                            }}
                          >
                            <span>CMI</span>
                            {sortColumn === 'cmi' &&
                              (sortDirection === 'asc' ? (
                                <ArrowUpwardIcon fontSize='small' />
                              ) : (
                                <ArrowDownwardIcon fontSize='small' />
                              ))}
                            <IconButton
                              onClick={e => {
                                e.stopPropagation()
                                setCmiFilterAnchor(e.currentTarget)
                              }}
                              size='small'
                            >
                              <FilterListIcon fontSize='small' />
                            </IconButton>
                          </th>
                          {/* Comment Column */}
                          <th
                            style={{
                              border: '1px solid #ccc',
                              padding: '8px',
                              width: '75%'
                            }}
                          >
                            Comment
                          </th>
                          {/* Relevancy Column Header */}
                          <th
                            style={{
                              border: '1px solid #ccc',
                              padding: '8px',
                              width: '15%',
                              textAlign: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (sortColumn === 'relevancy') {
                                setSortDirection(prev =>
                                  prev === 'asc' ? 'desc' : 'asc'
                                )
                              } else {
                                setSortColumn('relevancy')
                                setSortDirection('asc')
                              }
                            }}
                          >
                            Relevancy{' '}
                            {sortColumn === 'relevancy' &&
                              (sortDirection === 'asc' ? (
                                <ArrowUpwardIcon fontSize='small' />
                              ) : (
                                <ArrowDownwardIcon fontSize='small' />
                              ))}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDetails.map((detail, index) => (
                          <tr key={detail.key}>
                            <td
                              style={{
                                border: '1px solid #ccc',
                                padding: '8px',
                                fontWeight: 'bold',
                                color:
                                  detail.source === 'llm' ? 'green' : 'blue'
                              }}
                            >
                              {detail.cmi}
                            </td>
                            <td
                              style={{
                                border: '1px solid #ccc',
                                padding: '8px'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: sentenceCase(detail.original_comment)
                              }}
                            />
                            <td
                              style={{
                                border: '1px solid #ccc',
                                padding: '8px',
                                textAlign: 'center'
                              }}
                            >
                              <Tooltip
                                title={
                                  detail.relevancy
                                    ? detail.relevancy.charAt(0).toUpperCase() +
                                      detail.relevancy.slice(1)
                                    : ''
                                }
                              >
                                <span>
                                  {getRelevancyIcon(detail.relevancy)}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

            {actionTaken === 'Respond' && (
              <>
                <div className=''>
                  <p className='pb-8'>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: appetiteInfo?.Summary?.AppetiteSummary || ''
                      }}
                    />
                  </p>
                  <Button
                    onClick={() => handleClosePopup('Respond')}
                    variant='contained'
                    style={{
                      backgroundColor: 'green',
                      color: 'white',
                      marginBottom: '10px'
                    }}
                  >
                    Submit
                  </Button>
                </div>
              </>
            )}
            {actionTaken === 'Escalate' && (
              <>
                <div className=''>
                  <p className='pb-8'>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: appetiteInfo?.Summary?.AppetiteSummary || ''
                      }}
                    />
                  </p>
                  <Button
                    onClick={() => handleClosePopup('Escalate')}
                    variant='contained'
                    style={{
                      backgroundColor: 'green',
                      color: 'white',
                      marginBottom: '10px'
                    }}
                  >
                    Submit
                  </Button>
                </div>
              </>
            )}
            {cardType.includes('Desk') && (
              <div className='document-details' style={{ padding: '5px' }}>
                {detailsArray && Object.keys(detailsArray).length > 0 ? (
                  Object.keys(detailsArray).map((fileKey, i, arr) => {
                    const file = detailsArray[fileKey]
                    return (
                      <div
                        key={fileKey}
                        style={{
                          marginBottom: '20px',
                          paddingBottom: '10px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <h4
                            className='text-orange-600 italic'
                            style={{ margin: 0 }}
                          >
                            {file.filename}
                          </h4>
                          <a
                            href={file.file_uri}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <PictureAsPdfIcon className='text-orange-600' />
                          </a>
                        </div>
                        <p style={{ margin: '4px 0', fontStyle: 'italic' }}>
                          <strong>Type:</strong> {file.document_type}
                        </p>
                        <div
                          style={{ marginTop: '10px' }}
                          dangerouslySetInnerHTML={{
                            __html: file.consolidated_content
                          }}
                        />
                        {i !== arr.length - 1 && (
                          <hr
                            style={{
                              marginTop: '20px',
                              borderTop: '1px solid #ccc'
                            }}
                          />
                        )}
                      </div>
                    )
                  })
                ) : (
                  <p>No document details available</p>
                )}
              </div>
            )}
          </div>

          {/* CMI Filter Popover */}
          <Popover
            open={Boolean(cmiFilterAnchor)}
            anchorEl={cmiFilterAnchor}
            onClose={() => setCmiFilterAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <div style={{ padding: '16px', maxWidth: '200px' }}>
              <TextField
                label='Search CMI'
                variant='outlined'
                size='small'
                value={cmiSearch}
                onChange={e => setCmiSearch(e.target.value)}
                fullWidth
                style={{ marginBottom: '8px' }}
              />
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {filteredCMIs.map(cmi => (
                  <div
                    key={cmi}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Checkbox
                      checked={cmiFilter.includes(cmi)}
                      onChange={e => {
                        if (e.target.checked) {
                          setCmiFilter(prev => [...prev, cmi])
                        } else {
                          setCmiFilter(prev =>
                            prev.filter(item => item !== cmi)
                          )
                        }
                      }}
                    />
                    <span>{cmi}</span>
                  </div>
                ))}
              </div>
              {/* <Button
                onClick={() => setCmiFilterAnchor(null)}
                variant='contained'
                size='small'
                fullWidth
                style={{ marginTop: '8px' }}
              >
                Apply
              </Button> */}
            </div>
          </Popover>
        </DialogContent>
        {/* Sticky Footer */}
        {cardType.includes('Comments') && (
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background: '#f8ecdf',
              borderTop: '1px solid #ccc',
              padding: '8px',
              borderTop: '1px solid #ccc',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              gap: '25px'
            }}
          >
            <div>Line Of Business: {sortedDetails[0]?.lob || 'N/A'} </div>
            <div>Rate Date: {sortedDetails[0]?.ratedate || 'N/A'} </div>
            <div>
              State:{' '}
              {stateNameMap[sortedDetails[0]?.state_name] ||
                sortedDetails[0]?.state_name ||
                'N/A'}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  )
}

export default AnalysisCard
